import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { BookOpen, Bot, Check, Copy, KeyRound, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { ExperimentalBadge } from "@/components/experimental-badge";
import { getUser } from "@/functions/get-user";
import { authClient } from "@/lib/auth-client";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Input } from "@super-resume-new/ui/components/input";
import { Label } from "@super-resume-new/ui/components/label";
import { Spinner } from "@super-resume-new/ui/components/spinner";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/api-keys")({
  beforeLoad: async () => {
    const session = await getUser();
    if (!session) {
      throw redirect({ to: "/" });
    }
    return { session };
  },
  head: () =>
    seo({
      title: "API Key Management | JobCues",
      description:
        "Generate, rotate, and manage secret API keys for programmatic resume tailoring and ATS match scoring.",
      path: "/api-keys",
      noindex: true,
    }),
  component: ApiKeysComponent,
});

interface ApiKeyRecord {
  id: string;
  name?: string | null;
  start?: string | null;
  prefix?: string | null;
  createdAt: string | Date;
  lastRequest?: string | Date | null;
}

function ApiKeysComponent() {
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [keyName, setKeyName] = useState("");

  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedMcp, setCopiedMcp] = useState(false);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const res = await authClient.apiKey.list({});
      if (res.data?.apiKeys) {
        setKeys(res.data.apiKeys as ApiKeyRecord[]);
      }
    } catch (err) {
      console.error("Failed to list API keys:", err);
      toast.error("Failed to fetch API keys.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyName.trim()) {
      toast.error("Please enter an API Key name.");
      return;
    }

    setCreating(true);
    try {
      const res = await authClient.apiKey.create({
        name: keyName.trim(),
        prefix: "sk_live",
      });

      if (res.data?.key) {
        setNewlyCreatedKey(res.data.key);
        setKeyName("");
        toast.success("API Key generated successfully!");
        fetchKeys();
      } else {
        toast.error("Failed to generate API Key.");
      }
    } catch (err) {
      console.error("Error creating API key:", err);
      toast.error("Error generating API key.");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = async (id: string) => {
    if (
      !confirm("Are you sure you want to revoke this API Key? Agents using it will lose access.")
    ) {
      return;
    }

    try {
      await authClient.apiKey.delete({ keyId: id });
      toast.success("API Key revoked.");
      fetchKeys();
    } catch (err) {
      console.error("Failed to delete key:", err);
      toast.error("Failed to revoke API Key.");
    }
  };

  const copyToClipboard = (text: string, setCopied: (val: boolean) => void) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const mcpEndpointUrl =
    typeof window !== "undefined" ? `${window.location.origin}/api/mcp` : "/api/mcp";

  const sampleMcpConfig = JSON.stringify(
    {
      mcpServers: {
        "super-resume": {
          url: mcpEndpointUrl,
          headers: {
            Authorization: `Bearer ${newlyCreatedKey || "sk_live_your_api_key_here"}`,
          },
        },
      },
    },
    null,
    2,
  );

  return (
    <PageLayout id="api-keys-content" className="max-w-7xl">
      <PageHeader
        title="API keys and MCP"
        description="Connect custom AI agents such as Claude Desktop or Cursor to JobCues over the Model Context Protocol."
        icon={KeyRound}
        badge={<ExperimentalBadge />}
        actions={
          <Link to="/api-docs">
            <Button variant="outline" size="sm" className="gap-1.5">
              <BookOpen className="size-4" aria-hidden="true" />
              API docs
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Column: Key Generation & Active Keys Card */}
        <div className="lg:col-span-6 xl:col-span-7 flex flex-col gap-6">
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col gap-6">
            <div>
              <h2 className="mb-1 text-sm font-semibold text-foreground">
                <KeyRound className="mr-1.5 inline size-3.5 align-[-0.15em]" aria-hidden="true" />
                API key management
              </h2>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                Create secure access keys to authenticate your agent requests.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleCreateKey} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label htmlFor="key-name-input" className="sr-only">
                  Key Name
                </Label>
                <Input
                  id="key-name-input"
                  type="text"
                  placeholder="Key name (e.g. Claude Desktop Agent, Cursor IDE)"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  disabled={creating}
                  nativeInput
                />
              </div>
              <Button type="submit" disabled={creating} loading={creating} className="shrink-0">
                {!creating && <Plus className="mr-1 size-4" aria-hidden="true" />}
                Generate key
              </Button>
            </form>

            {/* Newly Created Key Banner */}
            {newlyCreatedKey && (
              <div className="rounded-xl border border-border bg-muted/40 p-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-foreground">
                    <ShieldCheck
                      className="mr-1.5 inline size-3.5 align-[-0.15em] text-emerald-500"
                      aria-hidden="true"
                    />
                    New key generated
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    Copy it now — it is not shown again
                  </span>
                </div>
                <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs text-foreground">
                  <span className="flex-1 select-all truncate">{newlyCreatedKey}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(newlyCreatedKey, setCopiedKey)}
                    className="h-7 text-xs"
                  >
                    {copiedKey ? (
                      <Check className="size-3.5 text-emerald-500" aria-hidden="true" />
                    ) : (
                      <Copy className="size-3.5" aria-hidden="true" />
                    )}
                    {copiedKey ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
            )}

            {/* Active Keys Listing */}
            <div className="border-t border-border pt-5">
              <h3 className="mb-3 text-sm font-semibold text-foreground">
                Active keys{" "}
                <span className="font-mono text-[11px] text-muted-foreground">{keys.length}</span>
              </h3>

              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Spinner className="size-5 text-muted-foreground" />
                </div>
              ) : keys.length === 0 ? (
                <p className="py-3 text-center text-[13px] text-muted-foreground">
                  No active API keys yet. Create one above to get started.
                </p>
              ) : (
                <div className="flex flex-col divide-y divide-border">
                  {keys.map((k) => (
                    <div key={k.id} className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg border border-border bg-muted p-2">
                          <KeyRound className="size-4 text-muted-foreground" aria-hidden="true" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground">
                              {k.name || "API key"}
                            </span>
                            <Badge variant="outline" className="font-mono text-[10px]">
                              {k.prefix ? `${k.prefix}_...` : "sk_live_..."}
                              {k.start ? k.start : ""}
                            </Badge>
                          </div>
                          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                            Created {new Date(k.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteKey(k.id)}
                        aria-label={`Delete ${k.name || "API key"}`}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: MCP Configuration Card */}
        <div className="lg:col-span-6 xl:col-span-5 flex flex-col gap-6">
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col gap-5">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-foreground">
                <Bot className="mr-1.5 inline size-3.5 align-[-0.15em]" aria-hidden="true" />
                MCP integration setup
              </h2>
              <ExperimentalBadge text="Beta" />
            </div>

            {/* Step 1: Endpoint */}
            <div className="flex flex-col gap-2">
              <Label className="text-[11px] text-muted-foreground">Server endpoint</Label>
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 font-mono text-xs text-foreground">
                <span className="flex-1 truncate">{mcpEndpointUrl}</span>
                <button
                  type="button"
                  aria-label="Copy server endpoint"
                  onClick={() => copyToClipboard(mcpEndpointUrl, setCopiedMcp)}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copiedMcp ? (
                    <Check className="size-3.5 text-emerald-500" aria-hidden="true" />
                  ) : (
                    <Copy className="size-3.5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            {/* Step 2: Available Tools */}
            <div className="flex flex-col gap-2">
              <Label className="text-[11px] text-muted-foreground">Exposed agent tools</Label>
              <ul className="divide-y divide-border rounded-lg border border-border bg-muted/40 px-3 text-xs">
                {[
                  "resume_tailor",
                  "ats_booster",
                  "extract_ats_keywords",
                  "add_job_application",
                ].map((tool) => (
                  <li key={tool} className="py-2">
                    <code className="font-mono text-foreground">{tool}</code>
                  </li>
                ))}
              </ul>
            </div>

            {/* Config JSON */}
            <div className="flex flex-col gap-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <Label className="text-[11px] text-muted-foreground">Claude or Cursor config</Label>
                <button
                  type="button"
                  onClick={() => copyToClipboard(sampleMcpConfig, setCopiedMcp)}
                  className="flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Copy className="size-3" aria-hidden="true" />
                  Copy config
                </button>
              </div>
              <pre className="overflow-x-auto rounded-xl border border-border bg-muted/40 p-4 font-mono text-[11px] leading-relaxed text-foreground">
                {sampleMcpConfig}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
